import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { LuArrowLeft } from 'react-icons/lu';
import usePostAdminImportRequestItemCreate from '../../api/usePostAdminImportRequestItemCreate';
import AdminErrorMessage from '../../components/atoms/AdminErrorMessage';
import AdminBackLink from '../../components/atoms/AdminBackLink';
import AdminContentEntryList from '../../components/molecules/AdminContentEntryList';
import {
  mergeContentEntries,
  type ContentEntry,
} from '../../components/molecules/AdminContentEntryList';
import AdminHtmlParseArea from '../../components/molecules/AdminHtmlParseArea';

type ItemCreatePageProps = {
  importRequestId: number;
};

export default function ItemCreatePage({ importRequestId }: ItemCreatePageProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);

  // フォームの状態
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobContent, setJobContent] = useState<ContentEntry[]>([]);
  const [agentContent, setAgentContent] = useState<ContentEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { mutate: postCreateItem, isPending: isCreatePending } =
    usePostAdminImportRequestItemCreate();

  const handleParsed = (result: {
    job_content: ContentEntry[];
    agent_content: ContentEntry[];
    title: string | null;
    company_name: string | null;
  }) => {
    setJobContent((prev) => mergeContentEntries(prev, result.job_content));
    setAgentContent((prev) => mergeContentEntries(prev, result.agent_content));
    if (result.title) setTitle(result.title);
    if (result.company_name) setCompanyName(result.company_name);
    setStep(2);
  };

  const handleBackToStep1 = () => {
    setStep(1);
  };

  const handleSubmit = () => {
    const trimmedTitle = title.trim();
    const trimmedUrl = url.trim();
    const trimmedCompanyName = companyName.trim();
    if (!trimmedTitle || !trimmedUrl || !trimmedCompanyName) {
      setError('タイトル・URL・企業名は必須です');
      return;
    }
    setError(null);
    const jobContentPayload = jobContent.map(({ label, content }) => ({
      key: label.trim() || undefined,
      label: label.trim() || undefined,
      content: content.trim() || undefined,
    }));
    const agentContentPayload = agentContent.map(({ label, content }) => ({
      key: label.trim() || undefined,
      label: label.trim() || undefined,
      content: content.trim() || undefined,
    }));
    postCreateItem(
      {
        importRequestId,
        outer_job_post: {
          title: trimmedTitle,
          url: trimmedUrl,
          company_name: trimmedCompanyName,
          job_content: jobContentPayload.length > 0 ? jobContentPayload : undefined,
          agent_content: agentContentPayload.length > 0 ? agentContentPayload : undefined,
        },
      },
      {
        onSuccess: () => {
          navigate({
            to: '/admin',
            search: { tab: 'import_requests', importRequestId },
          });
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : '求人の追加に失敗しました');
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <AdminBackLink to="/admin" search={{ tab: 'import_requests', importRequestId }} />
      <div className="rounded border border-solid border-border bg-white p-6">
        <h2 className="mb-4 text-lg font-bold text-main">求人を追加する</h2>

        {step === 1 && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-sub">
              求人ページのHTMLを貼り付けてパースしてください。パース結果をもとにフォームが自動入力されます。
            </p>
            <AdminHtmlParseArea importRequestId={importRequestId} onParsed={handleParsed} />
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={handleBackToStep1}
              className="inline-flex items-center gap-1 self-start border-0 bg-transparent p-0 text-sm text-sub hover:text-main"
            >
              <LuArrowLeft className="size-4" />
              HTML入稿に戻る
            </button>
            <div className="flex flex-col gap-3 text-sm">
              <div>
                <label htmlFor="create-title" className="mb-1 block font-medium text-main">
                  タイトル <span className="text-red-500">*</span>
                </label>
                <input
                  id="create-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="求人タイトル"
                  className="box-border w-full rounded border border-solid border-border px-3 py-2 text-main"
                />
              </div>
              <div>
                <label htmlFor="create-url" className="mb-1 block font-medium text-main">
                  URL <span className="text-red-500">*</span>
                </label>
                <input
                  id="create-url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="box-border w-full rounded border border-solid border-border px-3 py-2 text-main"
                />
              </div>
              <div>
                <label htmlFor="create-company" className="mb-1 block font-medium text-main">
                  企業名 <span className="text-red-500">*</span>
                </label>
                <input
                  id="create-company"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="企業名"
                  className="box-border w-full rounded border border-solid border-border px-3 py-2 text-main"
                />
              </div>
              <AdminContentEntryList
                title="求人情報"
                entries={jobContent}
                onChange={setJobContent}
                keyPrefix="job"
              />
              <AdminContentEntryList
                title="エージェント向け項目"
                entries={agentContent}
                onChange={setAgentContent}
                keyPrefix="agent"
              />
              <AdminErrorMessage message={error} />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() =>
                  navigate({
                    to: '/admin',
                    search: { tab: 'import_requests', importRequestId },
                  })
                }
                className="rounded border border-solid border-border bg-white px-4 py-2 text-sm text-main hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isCreatePending}
                className="rounded border-0 bg-primary px-4 py-2 text-sm text-white hover:opacity-90 disabled:opacity-50"
              >
                {isCreatePending ? '登録中...' : '登録する'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
